import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { SignatureCard } from '@common/SignatureCard';
import { AxiosErrorResponse } from '@core/error.models';
import { Note } from '@core/Note/Note';
import { ComponentType, FormFieldMethods, FormTextarea, IItemResponse } from '@gcl/react-core';
import { UseMutationResult } from '@tanstack/react-query';
import * as React from 'react';
import { IT4TopBtnsVisibility } from './permitToLoadRemovalDetailPage.hooks';
import { ECreateT4FormKeys, EPermitToLoadRemoveDetailPermitType } from './permitToLoadRemoveDetailPage.constants';
import {
  IProjectLoadingRemovalCert,
  IProjectLoadingRemovalCertS,
  IUpdateProjectLoadingRemovalCertRequest,
} from './permitToLoadRemoveDetailPage.models';
import './PermitToLoadRemoveDetailPage.scss';

interface IPermitToLoadRemoveDetailPermitProps {
  formFieldMethods: FormFieldMethods;
  isReadOnly: boolean;
  type: EPermitToLoadRemoveDetailPermitType;
  permitDetail?: IProjectLoadingRemovalCert | IProjectLoadingRemovalCertS;
  mutationApproveTwc: UseMutationResult<
    IItemResponse<IProjectLoadingRemovalCert>,
    AxiosErrorResponse,
    IUpdateProjectLoadingRemovalCertRequest,
    unknown
  >;
  mutationIssueSrp: UseMutationResult<
    IItemResponse<IProjectLoadingRemovalCert>,
    AxiosErrorResponse,
    IUpdateProjectLoadingRemovalCertRequest,
    unknown
  >;
  onClickIssueSrp: React.MouseEventHandler<HTMLButtonElement>;
  onClickApproveTwc: React.MouseEventHandler<HTMLButtonElement>;
  isRemovalCert: boolean;
  mode: DetailPageAccessMode;
  t4TopBtnsVisibility: IT4TopBtnsVisibility;
  isEnableTwcComment: boolean;
}
export const PermitToLoadRemoveDetailPermit: React.FunctionComponent<IPermitToLoadRemoveDetailPermitProps> = ({
  formFieldMethods,
  isReadOnly,
  type,
  permitDetail,
  mutationApproveTwc,
  mutationIssueSrp,
  onClickIssueSrp,
  onClickApproveTwc,
  isRemovalCert,
  mode,
  t4TopBtnsVisibility,
  isEnableTwcComment,
}) => {
  const isPermitToRemoveRequired: boolean = formFieldMethods.watch(ECreateT4FormKeys.PERMIT_TO_REMOVE_REQUIRED);

  // let issueToTwcBtn: React.ReactElement | undefined =
  //   mode === 'Update' && t4TopBtnsVisibility.isShowIssueToTwcBtn ? (
  //     <div className="gcl-gems-permit-to-load-remove-permit-card-content-button-container">
  //       <LoadingButton
  //         isSuccess={mutationIssueSrp.isSuccess}
  //         isError={mutationIssueSrp.isError}
  //         isLoading={mutationIssueSrp.isLoading}
  //         type={ComponentType.Primary}
  //         onClick={onClickIssueSrp}
  //         timeToResetSuccessState={3000}
  //         timeToResetErrorState={5000}
  //       >
  //         Issue to TWC
  //       </LoadingButton>
  //     </div>
  //   ) : undefined;
  // let approveBtn: React.ReactElement | undefined =
  //   mode === 'Update' && t4TopBtnsVisibility.isShowApproveBtn ? (
  //     <div className="gcl-gems-permit-to-load-remove-permit-card-content-button-container">
  //       <LoadingButton
  //         isSuccess={mutationApproveTwc.isSuccess}
  //         isError={mutationApproveTwc.isError}
  //         isLoading={mutationApproveTwc.isLoading}
  //         type={ComponentType.Primary}
  //         onClick={onClickApproveTwc}
  //         timeToResetSuccessState={3000}
  //         timeToResetErrorState={5000}
  //       >
  //         Approve
  //       </LoadingButton>
  //     </div>
  //   ) : undefined;

  let title = '';
  let certifiedByTwcContent = '';
  let remarkFormKey = '';
  switch (type) {
    case EPermitToLoadRemoveDetailPermitType.load:
      title = 'Permit to Load';
      certifiedByTwcContent =
        'These Temporary Works have been checked by me and, as far as I can ascertain, they are in accordance with the documents noted above.';
      remarkFormKey = ECreateT4FormKeys.PERMIT_TO_LOAD_REMARK;
      // if (permitDetail?.isRemovalCert) {
      //   approveBtn = undefined;
      //   issueToTwcBtn = undefined;
      // }
      break;
    case EPermitToLoadRemoveDetailPermitType.remove:
    default:
      title = 'Permit to Remove';
      certifiedByTwcContent = 'I certify that the temporary works have been checked by me and may be safely unloaded.';
      remarkFormKey = ECreateT4FormKeys.PERMIT_TO_REMOVE_REMARK;

      // if (!isPermitToRemoveRequired) {
      //   issueToTwcBtn = (
      //     <Note className="gcl-mb-md" componentType={ComponentType.Primary}>
      //       NOT REQUIRED
      //     </Note>
      //   );
      //   approveBtn = (
      //     <Note className="gcl-mb-md" componentType={ComponentType.Primary}>
      //       NOT REQUIRED
      //     </Note>
      //   );
      // }
      // if (isPermitToRemoveRequired && !isRemovalCert) {
      //   issueToTwcBtn = undefined;
      //   approveBtn = undefined;
      // }

      break;
  }

  return (
    <>
      <b>{title}</b>
      <SignatureCard
        className="gcl-my-md"
        title="Initiated by SRP"
        signUser={permitDetail?.siteEngineerProjectUserProfile?.userProfile?.displayName}
        signDate={permitDetail?.siteEngineerDateTime}
        signProfile={permitDetail?.siteEngineerProjectUserProfile?.userProfile?.signatureUserProfile}
        onRenderContent={(signatureComponent) => {
          return (
            <div className="gcl-gems-permit-to-load-remove-permit-card-content">
              <div className="gcl-mb-md">
                These Temporary Works have been checked by me and, as far as I can ascertain, they are in accordance
                with the documents noted above.
              </div>
              {type === EPermitToLoadRemoveDetailPermitType.remove && !isPermitToRemoveRequired && (
                <Note className="gcl-mb-md" componentType={ComponentType.Primary}>
                  NOT REQUIRED
                </Note>
              )}
              {type === EPermitToLoadRemoveDetailPermitType.load && (
                <Note className="gcl-mb-md" componentType={ComponentType.Primary}>
                  Only the SRP nominated in the T1 can sign here.
                </Note>
              )}

              {signatureComponent}
            </div>
          );
        }}
      />
      <SignatureCard
        title="Certified by TWC"
        signUser={permitDetail?.twcProjectUserProfile?.userProfile?.displayName}
        signDate={permitDetail?.twcDateTime}
        signProfile={permitDetail?.twcProjectUserProfile?.userProfile?.signatureUserProfile}
        onRenderContent={(signatureComponent) => {
          return (
            <div className="gcl-gems-permit-to-load-remove-permit-card-content">
              <div className="gcl-mb-md">{certifiedByTwcContent}</div>
              {type === EPermitToLoadRemoveDetailPermitType.remove && !isPermitToRemoveRequired && (
                <Note className="gcl-mb-md" componentType={ComponentType.Primary}>
                  NOT REQUIRED
                </Note>
              )}
              {signatureComponent}

              <div>
                <LabelWithMessage labelFor={remarkFormKey} text="Remark" />
                <FormTextarea
                  formFieldMethods={formFieldMethods}
                  id={remarkFormKey}
                  value={formFieldMethods.getValues(remarkFormKey)}
                  disabled={!isEnableTwcComment}
                />
              </div>
            </div>
          );
        }}
      />
    </>
  );
};
